import React, { useState } from 'react';
import { AD_PRICE_FORMATTED } from '../utils/config';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPayment: (method: 'crypto' | 'card', telegramUsername?: string) => Promise<{ success: boolean; url?: string; error?: string }>;
  shortAdId?: string;
}

const PaymentModal: React.FC<PaymentModalProps> = ({ isOpen, onClose, onPayment, shortAdId }) => {
  const [loadingMethod, setLoadingMethod] = useState<string | null>(null);
  const [telegramUsername, setTelegramUsername] = useState('');
  const [paymentLink, setPaymentLink] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<'crypto' | 'card' | null>(null);

  const handlePaymentSelect = async (method: 'crypto' | 'card') => {
    setLoadingMethod(method);
    setPaymentMethod(method);
    
    try {
      // Pass the telegram username if it exists (remove @ prefix if user typed it)
      const username = telegramUsername.replace('@', '').trim();
      const result = await onPayment(method, username || undefined);
      
      if (result.success && result.url) {
        setPaymentLink(result.url);
        // Don't close modal, show the payment link instead
      } else if (result.error) {
        console.error('Payment error:', result.error);
        setPaymentLink(null);
      }
    } catch (error) {
      console.error('Payment error:', error);
      setPaymentLink(null);
    } finally {
      setLoadingMethod(null);
    }
  };

  const handleTelegramChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    // Remove @ if user types it, we'll add it back in the display
    if (value.startsWith('@')) {
      value = value.substring(1);
    }
    setTelegramUsername(value);
  };

  const handleBackToSelection = () => {
    setPaymentLink(null);
    setPaymentMethod(null);
  };

  const handleCopyLink = () => {
    if (paymentLink) {
      navigator.clipboard.writeText(paymentLink);
      // Could add a toast notification here
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal active">
      <div className="modal-content">
        <div className="modal-header">
          <h3>{paymentLink ? 'Complete Payment' : 'Choose Payment Method'}</h3>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>
        
        <div className="modal-body">
          {paymentLink ? (
            // Payment Link Display
            <div className="payment-link-section">
              <div className="payment-link-header">
                <div className="payment-link-icon">
                  {paymentMethod === 'crypto' ? '₿' : '💳'}
                </div>
                <div className="payment-link-title">
                  {paymentMethod === 'crypto' ? 'Cryptocurrency Payment' : 'Card Payment'}
                </div>
                {shortAdId && (
                  <div className="payment-link-ad-id">
                    <span className="ad-id-label">Ad ID:</span>
                    <div className="ad-id-container">
                      <span className="ad-id-value">{shortAdId}</span>
                      <button 
                        className="ad-id-copy"
                        onClick={() => navigator.clipboard.writeText(shortAdId)}
                        title="Copy Ad ID"
                      >
                        📋
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="payment-link-message">
                <p>
                  {paymentMethod === 'crypto' 
                    ? 'Complete your cryptocurrency payment to activate your ad.' 
                    : 'Complete your card payment to activate your ad.'
                  }
                </p>
                <p className="payment-link-note">
                  If the payment page didn't open automatically, click the button below to pay.
                  Your ad will be received and processed immediately after payment.
                </p>
              </div>
              
              <div className="payment-link-actions">
                <button 
                  className="payment-link-button primary"
                  onClick={() => window.open(paymentLink, '_blank')}
                >
                  <span>🚀</span>
                  Open Payment Page
                </button>
                
                <button 
                  className="payment-link-button secondary"
                  onClick={handleCopyLink}
                >
                  <span>📋</span>
                  Copy Payment Link
                </button>
              </div>
              
              <div className="payment-link-footer">
                <button 
                  className="payment-link-back"
                  onClick={handleBackToSelection}
                >
                  ← Back to payment methods
                </button>
              </div>
            </div>
          ) : (
            // Original Payment Selection
            <>
              {/* Telegram Username Input */}
              <div className="telegram-input-section">
                <div className="telegram-input-container">
                  <span className="telegram-at">@</span>
                  <input
                    type="text"
                    value={telegramUsername}
                    onChange={handleTelegramChange}
                    placeholder="Your telegram username (optional)"
                    className="telegram-input"
                    maxLength={32}
                  />
                </div>
              </div>

              <div className="payment-options">
                <button
                  className={`payment-option ${loadingMethod === 'crypto' ? 'loading' : ''}`}
                  onClick={() => handlePaymentSelect('crypto')}
                  disabled={loadingMethod !== null}
                >
                  <div className="payment-icon">₿</div>
                  <div className="payment-info">
                    <div className="payment-label">Cryptocurrency</div>
                    <div className="payment-desc">Accept 200+ cryptos across all networks, instant, no KYC</div>
                  </div>
                </button>
                
                <button
                  className={`payment-option ${loadingMethod === 'card' ? 'loading' : ''}`}
                  onClick={() => handlePaymentSelect('card')}
                  disabled={loadingMethod !== null}
                >
                  <div className="payment-icon">💳</div>
                  <div className="payment-info">
                    <div className="payment-label">Bank Card</div>
                    <div className="payment-desc">Secure payment with Visa, Mastercard, and more</div>
                  </div>
                </button>
              </div>
              
              <div className="support-message">
                <p>Need help? Contact us <a href="https://t.me/neodia_ad" target="_blank" rel="noopener noreferrer">@neodia_ad</a> in telegram - reply in minutes</p>
              </div>
              
              <div className="pricing-info">
                <div className="price-breakdown">
                  <div className="price-item">
                    <span>Advertisement placement</span>
                    <span>{AD_PRICE_FORMATTED}</span>
                  </div>
                  <div className="price-item">
                    <span>48-hour premium slot</span>
                    <span>Included</span>
                  </div>
                  <div className="price-item">
                    <span>2-hour exclusivity</span>
                    <span>Included</span>
                  </div>
                </div>
                <div className="price-total">
                  <span>Total</span>
                  <span>{AD_PRICE_FORMATTED}</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentModal; 